import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function LabelManager({ user, onClose, onLabelsUpdated }) {
  const [labels, setLabels] = useState([]);
  const [newLabel, setNewLabel] = useState({ name: '', color: '#3b82f6' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLabels();
  }, []);

  const fetchLabels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/emails/labels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabels(response.data.labels || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching labels:', error);
      setLoading(false);
    }
  };

  const handleCreateLabel = async (e) => {
    e.preventDefault();
    if (!newLabel.name.trim()) {
      alert('Please enter a label name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/emails/labels`, newLabel, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewLabel({ name: '', color: '#3b82f6' });
      fetchLabels();
      if (onLabelsUpdated) onLabelsUpdated();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating label');
    }
  };

  const handleDeleteLabel = async (labelId) => {
    if (window.confirm('Delete this label? It will be removed from all emails.')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/emails/labels/${labelId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchLabels();
        if (onLabelsUpdated) onLabelsUpdated();
      } catch (error) {
        alert('Error deleting label');
      }
    }
  };

  const predefinedColors = [
    { name: 'Red', value: '#ef4444' },
    { name: 'Orange', value: '#f59e0b' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Purple', value: '#8b5cf6' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Gray', value: '#6b7280' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>🏷️ Manage Labels</h2>
          <button onClick={onClose} className="text-3xl text-gray-500 hover:text-gray-700">×</button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Create New Label */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">Create New Label</h3>
            <form onSubmit={handleCreateLabel} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label Name</label>
                <input
                  type="text"
                  value={newLabel.name}
                  onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
                  placeholder="e.g., Urgent, Follow-up"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{ focusRingColor: COLORS.primary }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Label Color</label>
                <div className="flex gap-2 flex-wrap">
                  {predefinedColors.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setNewLabel({ ...newLabel, color: color.value })}
                      className={`w-10 h-10 rounded-full border-2 ${
                        newLabel.color === color.value ? 'border-gray-800' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="w-full px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                style={{ backgroundColor: COLORS.primary }}
              >
                ✨ Create Label
              </button>
            </form>
          </div>

          {/* Existing Labels */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Your Labels ({labels.length})</h3>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : labels.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No labels yet. Create your first label above!</div>
            ) : (
              <div className="space-y-2">
                {labels.map((label) => (
                  <div
                    key={label.id}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: label.color }}
                      />
                      <span className="font-medium text-gray-800">{label.name}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteLabel(label.id)}
                      className="text-red-500 hover:text-red-700 font-medium"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LabelManager;
